import { Box, InputBase, Stack, Typography, useTheme } from "@mui/material";
import type { ChangeEvent, KeyboardEvent, ReactNode, Ref } from "react";
import { VALUE_LINE_HEIGHT } from "web_myplann/Shared/Components/FlatRow/constants";

export type FlatRowInputProps = {
	label: string;
	value?: string;
	onChange?: (value: string) => void;
	onBlur?: () => void;
	inputRef?: Ref<HTMLInputElement>;
	placeholder?: string;
	suffix?: string;
	icon?: ReactNode;
	readOnly?: boolean;
	isLast?: boolean;
	inputMode?: "text" | "numeric" | "decimal" | "tel" | "email";
	error?: boolean;
	helperText?: string;
};

const VALUE_MAX_LINES_EDIT = 6;
const NEWLINE_PATTERN = /\r?\n/g;

export const FlatRowInput = ({
	label,
	value,
	onChange,
	onBlur,
	inputRef,
	placeholder,
	suffix,
	icon,
	readOnly,
	isLast,
	inputMode,
	error,
	helperText,
}: FlatRowInputProps) => {
	const theme = useTheme();

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		// multiline textarea accepts newlines via paste / voice input — flatten to one line
		onChange?.(event.target.value.replace(NEWLINE_PATTERN, " "));
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			event.currentTarget.blur();
		}
	};

	return (
		<Box borderBottom={isLast ? "none" : theme.colors.border.flatRowDivider}>
			<Stack
				component="label"
				direction="row"
				alignItems="flex-start"
				justifyContent="space-between"
				width="100%"
				px={1.6}
				py={1.4}
				minHeight={48}
				sx={{
					cursor: readOnly ? "pointer" : "text",
					"&:active": readOnly
						? { bgcolor: theme.palette.deprecated.grey[50] }
						: undefined,
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					gap={0.6}
					flexShrink={0}
					mr={2}
				>
					<Typography
						variant="deprecatedBody1"
						color={
							error
								? theme.palette.deprecated.error.main
								: theme.palette.deprecated.grey[500]
						}
						lineHeight={`${VALUE_LINE_HEIGHT}px`}
					>
						{label}
					</Typography>
				</Stack>

				<Stack
					direction="row"
					alignItems="flex-start"
					gap={0.6}
					flex={1}
					minWidth={0}
				>
					{readOnly ? (
						<Typography
							variant="deprecatedBodyMedium"
							color={
								value
									? theme.palette.text.primary
									: theme.palette.deprecated.grey[300]
							}
							sx={{
								flex: 1,
								minWidth: 0,
								textAlign: "right",
								lineHeight: `${VALUE_LINE_HEIGHT}px`,
								overflowWrap: "anywhere",
							}}
						>
							{value || placeholder || ""}
						</Typography>
					) : (
						<InputBase
							value={value ?? ""}
							onChange={handleChange}
							onBlur={onBlur}
							onKeyDown={handleKeyDown}
							inputRef={inputRef}
							placeholder={placeholder}
							multiline
							inputProps={{ inputMode }}
							sx={{
								flex: 1,
								minWidth: 0,
								p: 0,
								"& textarea": {
									p: 0,
									textAlign: "right",
									fontSize: 14,
									fontWeight: 500,
									lineHeight: `${VALUE_LINE_HEIGHT}px`,
									color: theme.palette.text.primary,
									overflowWrap: "anywhere",
									// cap is a safety net for legacy values — scroll, never clip
									maxHeight: VALUE_LINE_HEIGHT * VALUE_MAX_LINES_EDIT,
									overflowY: "auto",
									"&::placeholder": {
										color: theme.palette.deprecated.grey[300],
										fontWeight: 400,
										opacity: 1,
									},
								},
							}}
						/>
					)}
					{suffix && (
						<Typography
							variant="deprecatedBody1"
							color={theme.palette.deprecated.grey[400]}
							flexShrink={0}
							lineHeight={`${VALUE_LINE_HEIGHT}px`}
						>
							{suffix}
						</Typography>
					)}
					{icon && (
						<Box
							flexShrink={0}
							display="flex"
							alignItems="center"
							height={`${VALUE_LINE_HEIGHT}px`}
						>
							{icon}
						</Box>
					)}
				</Stack>
			</Stack>
			{error && helperText && (
				<Typography
					component="p"
					variant="deprecatedCaption"
					color={theme.palette.deprecated.error.main}
					px={1.6}
					pb={0.6}
				>
					{helperText}
				</Typography>
			)}
		</Box>
	);
};
